import { useQuery } from '@tanstack/react-query';

import { apiClient } from './api-client';

interface FeatureFlagCheckResponse {
  enabled: boolean;
}

export const featureFlagKeys = {
  check: (key: string) => ['feature-flags', key, 'check'] as const,
};

/**
 * Hook to check if a feature flag is enabled.
 * Caches the result for 60 seconds (matching backend TTL).
 *
 * While the flag is loading, `enabled` defaults to `defaultWhileLoading`
 * (false by default) to prevent UI flicker. Callers that want to keep
 * content visible until the flag is resolved can pass `true`.
 */
export function useFeatureFlag(key: string, defaultWhileLoading = false) {
  const query = useQuery({
    queryKey: featureFlagKeys.check(key),
    queryFn: () => apiClient.fetch<FeatureFlagCheckResponse>(`/feature-flags/${key}/check`),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
  });

  return {
    enabled: query.isLoading ? defaultWhileLoading : (query.data?.enabled ?? false),
    isLoading: query.isLoading,
  };
}
